/**
 * Social Feed service (SOCIAL_LAYER_PLAN §A / P2).
 *
 * Thin TanStack-Query wrappers over the governance-service social-feed API.
 * Every path is unversioned (`/feed`, `/social/*`) and resolves to
 * governance-service via the PATH_SERVICE_MAP governance fallback — ZERO gateway
 * change. Byte-identical across frontend-client and frontend-adminclient.
 */
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query'
import { get, post, del, uploadFile } from '@/lib/api/client'
import type { FeedScope, PostType } from '@/pages/feed/feed-utils'

// ── Types ────────────────────────────────────────────────────────────────────
export interface FeedAuthor {
  tenant_id?: string
  user_id?: string
  name?: string
  kind?: 'business' | 'client'
}
export interface FeedMedia { media_id?: string; public_url?: string; kind?: string }
export interface FeedEvent {
  starts_at?: string; ends_at?: string; location?: string; is_online?: boolean; url?: string
}
export interface FeedCounts { reactions: number; comments: number; reshares: number }

export interface FeedCard {
  id: string
  kind: 'post' | 'projection'
  source?: { type?: string; id?: string }
  post_type: PostType
  author: FeedAuthor
  title?: string | null
  body?: string | null
  media: FeedMedia[]
  link_url?: string | null
  event?: FeedEvent
  audience_scope?: string
  status?: string
  pinned?: boolean
  source_ref?: Record<string, unknown>
  extra?: Record<string, unknown>
  /** type-specific optional fields (deal / financement / info / …) */
  config?: Record<string, unknown>
  counts: FeedCounts
  created_at?: string
  published_at?: string
}

export interface FeedResponse {
  tenant_id: string
  scope: string
  items: FeedCard[]
  count: number
  next_cursor: string | null
  has_more: boolean
}

export interface FeedComment {
  id: string
  post_id: string
  user_id?: string
  name?: string
  body?: string
  parent_comment_id?: string | null
  created_at?: string
}

export interface CreatePostInput {
  post_type: PostType
  title?: string
  body?: string
  media?: FeedMedia[]
  link_url?: string
  event?: FeedEvent
  /** type-specific optional fields (deal / financement / info / …) */
  config?: Record<string, unknown>
  audience_scope?: 'global' | 'clients' | 'followers'
  author_kind?: 'business' | 'client'
}

// ── Query keys ───────────────────────────────────────────────────────────────
export const feedKeys = {
  all: ['social-feed'] as const,
  timeline: (scope: FeedScope, type?: string) =>
    ['social-feed', 'timeline', scope, type ?? 'all'] as const,
  comments: (postId: string) => ['social-feed', 'comments', postId] as const,
  follows: ['social-feed', 'follows'] as const,
  unread: ['social-feed', 'unread-head'] as const,
}

const PAGE_LIMIT = 12

// ── Timeline (infinite) ──────────────────────────────────────────────────────
export function useFeed(scope: FeedScope, type?: string) {
  return useInfiniteQuery({
    queryKey: feedKeys.timeline(scope, type),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      get<FeedResponse>('/feed', {
        params: {
          scope,
          type: type || undefined,
          cursor: pageParam || undefined,
          limit: PAGE_LIMIT,
        },
      }),
    getNextPageParam: (last) => (last?.has_more ? last?.next_cursor || undefined : undefined),
    staleTime: 20_000,
    refetchOnWindowFocus: true,
    // Live-ish: keep the top of the feed fresh without a WS dependency.
    refetchInterval: scope === 'for-you' || scope === 'global' ? 60_000 : false,
  })
}

/** Flatten infinite pages into a single card list. */
export function flattenFeed(pages?: { items: FeedCard[] }[]): FeedCard[] {
  if (!pages) return []
  return pages.flatMap((p) => p.items || [])
}

// ── Create / delete post ─────────────────────────────────────────────────────
export function useCreatePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePostInput) =>
      post<{ id: string; post: FeedCard; persisted: boolean }>('/social/posts', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.all })
    },
  })
}

export function useDeletePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (postId: string) => del(`/social/posts/${postId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedKeys.all }),
  })
}

// ── Interactions ─────────────────────────────────────────────────────────────
export interface ReactResult { reaction_count: number; persisted?: boolean }
export function reactToPost(postId: string, reaction_type = 'like') {
  return post<ReactResult>(`/social/posts/${postId}/react`, { reaction_type })
}
export function unreactToPost(postId: string, reaction_type = 'like') {
  return del<ReactResult>(`/social/posts/${postId}/react`, { params: { reaction_type } })
}
export interface BookmarkResult { bookmarked: boolean }
export function toggleBookmark(postId: string) {
  return post<BookmarkResult>(`/social/posts/${postId}/bookmark`, {})
}
export interface ReshareResult { reshare_count: number; reshare_id: string }
export function resharePost(postId: string, comment?: string) {
  return post<ReshareResult>(`/social/posts/${postId}/reshare`, { comment: comment || undefined })
}

// ── Comments ─────────────────────────────────────────────────────────────────
export function useComments(postId: string, enabled: boolean) {
  return useQuery({
    queryKey: feedKeys.comments(postId),
    enabled,
    queryFn: () =>
      get<{ items: FeedComment[]; total: number }>(`/social/posts/${postId}/comments`, {
        params: { limit: 200 },
      }),
    staleTime: 15_000,
  })
}
export function useAddComment(postId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      post<{ comment_id: string; comment_count: number }>(
        `/social/posts/${postId}/comments`, { body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedKeys.comments(postId) }),
  })
}

// ── Follows ──────────────────────────────────────────────────────────────────
export interface FollowRow { id: string; business_tenant_id: string; created_at?: string }
export function useFollows() {
  return useQuery({
    queryKey: feedKeys.follows,
    queryFn: () => get<{ items: FollowRow[] }>('/social/follows'),
    staleTime: 60_000,
  })
}
export function useFollowBusiness() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (business_tenant_id: string) =>
      post('/social/follows', { business_tenant_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.follows })
      qc.invalidateQueries({ queryKey: feedKeys.all })
    },
  })
}
export function useUnfollowBusiness() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (business_tenant_id: string) =>
      del(`/social/follows/${business_tenant_id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.follows })
      qc.invalidateQueries({ queryKey: feedKeys.all })
    },
  })
}

// ── Media upload ─────────────────────────────────────────────────────────────
export interface UploadedMedia {
  media_id: string
  public_url?: string
  url?: string
  kind?: string
}
export async function uploadFeedMedia(file: File): Promise<FeedMedia> {
  const res = await uploadFile<UploadedMedia>('/social/media', file)
  return {
    media_id: res.media_id,
    public_url: res.public_url || res.url,
    kind: res.kind || (file.type.startsWith('video/') ? 'video' : 'image'),
  }
}

// ── Top-bar unread indicator (lightweight, limit=1) ──────────────────────────
const LAST_SEEN_KEY = 'sgrc-feed-last-seen'
export function markFeedSeen() {
  try { localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString()) } catch { /* noop */ }
}
export function useFeedUnread() {
  return useQuery({
    queryKey: feedKeys.unread,
    queryFn: async () => {
      const res = await get<FeedResponse>('/feed', { params: { scope: 'for-you', limit: 1 } })
      const newest = res.items?.[0]?.created_at || null
      let lastSeen: string | null = null
      try { lastSeen = localStorage.getItem(LAST_SEEN_KEY) } catch { /* noop */ }
      return { newest, hasNew: !!newest && (!lastSeen || newest > lastSeen) }
    },
    staleTime: 30_000,
    refetchInterval: 90_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
}
